import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../hooks'
import { IntegrationFormFlow } from '../enums/integrationFormFlow'
import { SkatIntegrationStatus } from '../enums/skatIntegrationStatus'
import { useConnectSkat } from '../hooks/useConnectSkat'

interface SkatConnectionContextState {
  connectionState: SkatIntegrationStatus
  isAlreadyApproved: boolean
  isError: boolean
  isLoading: boolean
  isSkatConnected: boolean
  isValidCVR: boolean
  setConnectionState: (state: SkatIntegrationStatus) => void
  showSkatConnectionView: boolean
  showSkatDisconnectionView: boolean
  setSkatShowConnectionView: (show: boolean) => void
  setSkatShowDisconnectionView: (show: boolean) => void
  updateSkatConnection: (flow: IntegrationFormFlow) => void
}

const SkatConnectionContext = createContext<SkatConnectionContextState | undefined>(undefined)

interface SkatConnectionContextProps {
  children?: ReactNode
  skatTransactionId?: string
}

const VALID_REGISTRATION_NUMBER_LENGTH = 8

const SKAT_INTEGRATION_URL =
  'https://pdcs.skat.dk/dcs-atn-gateway/nemlogin?targetUrl=aHR0cHM6Ly9udHNlLnNrYXQuZGsvbnRzZS1mcm9udC9jb250ZW50P2lkPWZyYW1lOjJGQUF1dG9yaXNlcnJldmlzb3JtZmw='

export const SkatConnectionContextProvider = ({
  children,
  skatTransactionId,
}: SkatConnectionContextProps): ReactElement | null => {
  const { organization } = useUserOrganization()
  const { track } = useSegment()

  const [connectionState, setConnectionState] = useState(SkatIntegrationStatus.Idle)
  const [showSkatConnectionView, setSkatShowConnectionView] = useState(false)
  const [showSkatDisconnectionView, setSkatShowDisconnectionView] = useState(false)

  const isValidCVR = useMemo(() => {
    return (
      organization?.registrationNo?.length === VALID_REGISTRATION_NUMBER_LENGTH &&
      !isNaN(Number(organization?.registrationNo))
    )
  }, [organization?.registrationNo])

  const { skatConnection, isLoading, isError } = useConnectSkat({
    cacheTime: 0,
    isValidCVR,
    pollingEnabled: connectionState === SkatIntegrationStatus.Connecting,
    skatTransactionId,
    step: showSkatConnectionView ? 'Connecting' : 'Disconnecting',
  })

  useEffect(() => {
    /* Stop polling when connection view is open and we receive info that the connection is established */
    if (showSkatConnectionView && skatConnection?.connected) {
      setConnectionState(SkatIntegrationStatus.Idle)
      track('xxx - Bookkeeping - DK VAT integration', {
        flow: IntegrationFormFlow.Connect,
        step: '4 - Success',
      })
    }

    /* Stop polling when disconnection view is open and we receive info that the connection is removed */
    if (showSkatDisconnectionView && !skatConnection?.connected) {
      setConnectionState(SkatIntegrationStatus.Idle)
      track('xxx - Bookkeeping - DK VAT integration', {
        flow: IntegrationFormFlow.Disconnect,
        step: '4 - Success',
      })
    }
  }, [skatConnection, showSkatConnectionView, showSkatDisconnectionView, track])

  const updateSkatConnection = useCallback(
    (flow: IntegrationFormFlow) => {
      track('xxx - Bookkeeping - DK VAT integration', {
        flow,
        step: '2 - Redirected',
      })
      window.open(SKAT_INTEGRATION_URL, '_blank', 'noopener,noreferrer')
      track('xxx - Bookkeeping - DK VAT integration', {
        flow,
        step: '3 - Connecting',
      })
    },
    [track],
  )

  return (
    <SkatConnectionContext.Provider
      value={{
        connectionState,
        isSkatConnected: !!skatConnection?.connected,
        /* Is only valid when transaction id is provided */
        isAlreadyApproved: (skatTransactionId && skatConnection?.is_approved) || false,
        isLoading,
        isError,
        isValidCVR,
        setConnectionState,
        showSkatConnectionView,
        setSkatShowConnectionView,
        showSkatDisconnectionView,
        setSkatShowDisconnectionView,
        updateSkatConnection,
      }}
    >
      {children}
    </SkatConnectionContext.Provider>
  )
}

export const useSkatConnection = () => {
  const context = useContext(SkatConnectionContext)

  if (!context) {
    throw new Error('SkatConnectionContextProvider is missing in the module!')
  }

  return context
}
